import React, { useState, forwardRef, useContext } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  Autoformat,
  Base64UploadAdapter,
  Bold,
  CKFinderUploadAdapter,
  ClassicEditor,
  Enter,
  Essentials,
  Font,
  Heading,
  Highlight,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  InlineEditor,
  Italic,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  SimpleUploadAdapter,
  Subscript,
  Superscript,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Typing,
  Undo,
} from "ckeditor5";
import { Comments, TrackChanges } from "ckeditor5-premium-features";
import "ckeditor5/ckeditor5.css";
import "ckeditor5-premium-features/ckeditor5-premium-features.css";
import { IMAGE_UPLOAD_URL, getImageUploadHeaders } from "../../../services/upload/uploadImages";
import { Container, MaxLengthReachedMessageContainer } from "./styled";
import { EDITOR_LENGTH } from "../../../constants/miscellaneous";
import H5PEmbed from "./plugins/H5PEmbed";
import CollaborationContext from "../CollaborationContext";
import Loading from "../../presentational/Loading";
import { sanitizeContentComments, handleCollaborationInit, getCommentThreads, getSuggestions } from "./helpers";

const Editor = forwardRef((props, ref) => {
  const {
    className,
    learningUnitId,
    content,
    comments,
    suggestions,
    inline,
    basic,
    offsetTop,
    maxLength = Number.MAX_SAFE_INTEGER,
    isCommentable,
    isTrackable,
    onEditorCommentToolbarClick,
    onContentChange = () => {},
  } = props;

  const buffer = maxLength < EDITOR_LENGTH.BUFFER ? maxLength : maxLength - EDITOR_LENGTH.BUFFER;
  const isInitialLoad = React.useRef(false);
  const [contentLength, setContentLength] = useState(content && content.length ? content.length : 0);
  const [isLimitOver, setIsLimitOver] = useState(contentLength > buffer);
  const remainingChars = buffer - contentLength;

  const collaborationContext = useContext(CollaborationContext.Context);

  const { userId, users } = collaborationContext;
  if (isCommentable && (!collaborationContext || !userId || !users)) {
    return (
      <div style={{ display: "flex" }}>
        <Container $isLimitOver={isLimitOver}>
          <Loading />
        </Container>
      </div>
    );
  }

  const getConfig = (isBasicEditor, isCommentableEditor, isTrackableEditor) => {
    const basicConfig = {
      licenseKey: "eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MTQxNDA3OTksImp0aSI6ImMzZGVhN2IwLWJlMDktNGM1Yy1iMmJlLTJmODZjODJmYzg1NCIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIlRDIiwiQ01UIl0sInZjIjoiNTBkMDk0NmMifQ.ysS_gcqa7A0e9zq8J3u0Sglcfqok7zwO0GL9HWaZlkYvsmYSRnD_V9VgK35zbLz34jdKnBSTdnyqIQUUPWYrsQ",
      plugins: [
        Enter,
        Typing,
        Undo,
        Paragraph,
        Bold,
        Italic,
        Font,
        Highlight,
        Superscript,
        Subscript,
        Image,
        Essentials,
        List,
        Autoformat,
        ImageCaption,
        ImageStyle,
        ImageToolbar,
        ImageUpload,
        SimpleUploadAdapter,
        ImageResize,
      ],
      toolbar: {
        viewportTopOffset: offsetTop,
        items: [
          "bold",
          "italic",
          "fontFamily",
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "highlight",
          "subscript",
          "superscript",
          "|",
          "imageUpload",
          "|",
          "undo",
          "redo",
        ],
      },
      image: {
        toolbar: [
          "toggleImageCaption",
          "imageStyle:alignLeft",
          "imageStyle:alignCenter",
          "imageStyle:alignRight",
          "|",
          "imageTextAlternative",
        ],
        styles: ["alignLeft", "alignCenter", "alignRight"],
      },
      simpleUpload: {
        // The URL that the images are uploaded to.
        uploadUrl: IMAGE_UPLOAD_URL,
        // Headers sent along with the XMLHttpRequest to the upload server.
        headers: getImageUploadHeaders(learningUnitId),
      },
      // This value must be kept in sync with the language defined in webpack.config.js.
      language: "en",
      fontSize: {
        options: [9, 11, 13, "default", 17, 19, 21],
      },
    };

    const defaultConfig = {
      licenseKey: "eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MTQxNDA3OTksImp0aSI6ImMzZGVhN2IwLWJlMDktNGM1Yy1iMmJlLTJmODZjODJmYzg1NCIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIlRDIiwiQ01UIl0sInZjIjoiNTBkMDk0NmMifQ.ysS_gcqa7A0e9zq8J3u0Sglcfqok7zwO0GL9HWaZlkYvsmYSRnD_V9VgK35zbLz34jdKnBSTdnyqIQUUPWYrsQ",
      plugins: [
        Enter,
        Typing,
        Undo,
        Bold,
        Italic,
        Font,
        Highlight,
        Superscript,
        Subscript,
        Image,
        Essentials,
        Heading,
        Link,
        List,
        Base64UploadAdapter,
        Autoformat,
        CKFinderUploadAdapter,
        // BlockQuote,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        ImageCaption,
        ImageStyle,
        ImageToolbar,
        ImageUpload,
        Alignment,
        MediaEmbed,
        H5PEmbed,
        Indent,
        SimpleUploadAdapter,
        ImageResize,
      ],
      toolbar: {
        viewportTopOffset: offsetTop,
        items: [
          "heading",
          "bold",
          "italic",
          "fontFamily",
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "highlight",
          "subscript",
          "superscript",
          "link",
          "bulletedList",
          "numberedList",
          "|",
          "alignment",
          "indent",
          "outdent",
          "|",
          "imageUpload",
          // "blockQuote",
          "insertTable",
          "mediaEmbed",
          "h5pEmbed",
          "|",
          "undo",
          "redo",
          "|",
        ],
      },
      image: {
        toolbar: [
          "toggleImageCaption",
          "imageStyle:alignLeft",
          "imageStyle:alignCenter",
          "imageStyle:alignRight",
          "|",
          "imageTextAlternative",
        ],
        styles: ["alignLeft", "alignCenter", "alignRight"],
      },
      table: {
        contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"],
      },
      mediaEmbed: {
        removeProviders: ["instagram", "twitter", "googleMaps", "flickr", "facebook"],
        // setting previewsInData to 'true' prevents embed into becoming semantic oembed which then needs to be deciphered later
        previewsInData: true,
        extraProviders: [
          {
            name: "h5p",
            url: [/^h5p\.org\/(\d+)/, /^h5p\.org\/h5p\/embed\/(\d+)/, /^mobile-learning\.h5p\.com\/content\/(\d+)\/embed/],
            html: (match) => {
              const [url, id] = match;
              return `
              <div style="position: relative;">
              \n <iframe src="https://${url}" aria-label="KVD 1.1.2 Hotspot 1" width="1088" height="537" frameborder="0" allowfullscreen="allowfullscreen" allow="autoplay *; geolocation *; microphone *; camera *; encrypted-media *"></iframe>
              \n <script src="https://mobile-learning.h5p.com/js/h5p-resizer.js"charset="UTF-8"></script>
              </div>`;
            },
          },
          {
            name: "cloudfront",
            url: /https?:\/\/.*\/.*\.mp4/,
            html: (match) => {
              const [url, id] = match;
              return `
            <div style="position: relative;">
              \n <video style="max-width: 100%;" controls="" src="${url}"></video>
            </div>`;
            },
          },
        ],
      },
      simpleUpload: {
        // The URL that the images are uploaded to.
        uploadUrl: IMAGE_UPLOAD_URL,
        // Headers sent along with the XMLHttpRequest to the upload server.
        headers: getImageUploadHeaders(learningUnitId),
      },
      // This value must be kept in sync with the language defined in webpack.config.js.
      language: "en",
      heading: {
        options: [
          { model: "paragraph", title: "Normal text", class: "ck-heading_paragraph" },
          { model: "heading2", view: "h2", title: "Heading 1", class: "ck-heading_heading2" },
          { model: "heading3", view: "h3", title: "Heading 2", class: "ck-heading_heading3" },
          { model: "heading4", view: "h4", title: "Heading 3", class: "ck-heading_heading4" },
        ],
      },
      fontSize: {
        options: [9, 11, 13, "default", 17, 19, 21],
      },
    };

    const collaborationConfig = {
      ...defaultConfig,
      plugins: [...defaultConfig.plugins, Comments, TrackChanges],
      toolbar: {
        ...defaultConfig.toolbar,
        items: [...defaultConfig.toolbar.items, isCommentable ? "comment" : [], isTrackable ? "trackChanges" : []].flat(),
      },
      comments: {
        editorConfig: {},
      },
    };

    if (isBasicEditor) {
      return basicConfig;
    }
    if (isCommentableEditor || isTrackableEditor) {
      return collaborationConfig;
    }
    return defaultConfig;
  };

  return (
    <div style={{ display: "flex" }}>
      <Container $isLimitOver={isLimitOver}>
        <CKEditor
          className={className}
          editor={inline ? InlineEditor : ClassicEditor}
          config={getConfig(basic, isCommentable)}
          // data={content}
          onReady={(editor) => {
            // You can store the "editor" and use when it is needed.

            if (isCommentable && !!collaborationContext) {
              handleCollaborationInit(editor, {
                ...collaborationContext,
                sidebarElement: ref.current,
                onEditorCommentToolbarClick,
                comments,
                suggestions,
              });
            }

            // We need to load content after loading everything else (i.e. users, comments, suggestions)
            if (!!content) {
              isInitialLoad.current = true;
              editor.setData(sanitizeContentComments(content, comments));
              isInitialLoad.current = false;
            }
          }}
          onChange={(event, editor) => {
            if (isInitialLoad.current) return;
            const data = editor.getData();
            // set isLimitOver flag
            setIsLimitOver(data.length > buffer);
            setContentLength(data.length);
            // check data limit. only fire handler if length is within limit

            if (isCommentable && !!collaborationContext) {
              const updatedComments = getCommentThreads(editor);
              const updatedSuggestions = getSuggestions(editor);

              if (data.length <= buffer) {
                onContentChange({
                  content: data,
                  comments: JSON.stringify(updatedComments),
                  suggestions: JSON.stringify(updatedSuggestions),
                });
              }
            } else if (data.length <= buffer) {
              onContentChange({
                content: data,
              });
            }
          }}
          onBlur={(event, editor) => {
            // const trackChanges = editor.plugins.get("TrackChanges");
          }}
          onFocus={(event, editor) => {}}
        />
        {isLimitOver && <MaxLengthReachedMessageContainer>{remainingChars} character(s) over</MaxLengthReachedMessageContainer>}
      </Container>
    </div>
  );
});
export default Editor;
